/**
 * Access levels (docs/13-ACCESS.md): who can do what, and where.
 *
 * Each module has a ladder of levels, and each level includes those below.
 * A grant puts a person on one rung of one module's ladder, church-wide or
 * (for ForServices planning) on a folder or a service type. Where several
 * grants reach the same place, the highest wins; nothing lowers a level and
 * there are no deny rules.
 *
 * These are the rules only: no data, no I/O. The server resolves a person's
 * grants and a service type's folder chain, then asks these functions.
 * Giving stays outside the ladders: it is an explicit grant, as before.
 */
import type { Module, Permission, RoleId } from './index.ts'

// ---- ladders ---------------------------------------------------------------------------

/**
 * Every level, lowest first. Each ladder below is this order with some rungs
 * left out, so one ranking compares any two levels of the same ladder.
 */
export const LEVELS = [
  'scheduled_viewer',
  'viewer',
  'scheduler',
  'editor',
  'administrator',
] as const
export type Level = (typeof LEVELS)[number]

export const LADDERS = {
  /** ForServices "Planning & people": plans, teams, scheduling. */
  planning: ['scheduled_viewer', 'viewer', 'scheduler', 'editor', 'administrator'],
  /** ForServices songs, church-wide only. */
  songs: ['viewer', 'editor', 'administrator'],
  /** Every ForChurch module on a ladder. */
  church: ['viewer', 'editor', 'administrator'],
} as const satisfies Record<string, readonly Level[]>

export type Ladder = keyof typeof LADDERS
export type PlanningLevel = (typeof LADDERS.planning)[number]
export type SongsLevel = (typeof LADDERS.songs)[number]
export type ChurchLevel = (typeof LADDERS.church)[number]

/**
 * The modules on a ladder, by app. ForChurch's are its modules minus giving
 * (an explicit grant) and services (ForServices has its own two).
 */
export const ACCESS_MODULES = {
  services: ['planning', 'songs'],
  church: [
    'org',
    'people',
    'groups',
    'attendance',
    'events',
    'checkin',
    'messages',
    'media',
    'reports',
  ] satisfies readonly Module[],
} as const

export type AccessApp = keyof typeof ACCESS_MODULES
export type ServicesModule = (typeof ACCESS_MODULES.services)[number]
export type ChurchModule = (typeof ACCESS_MODULES.church)[number]
export type AccessModule = ServicesModule | ChurchModule

export const ACCESS_MODULE_IDS: readonly AccessModule[] = [
  ...ACCESS_MODULES.services,
  ...ACCESS_MODULES.church,
]

/** The levels a module's ladder has. */
export type LevelOf<M extends AccessModule> = M extends 'planning'
  ? PlanningLevel
  : M extends 'songs'
    ? SongsLevel
    : ChurchLevel

/** The app a module belongs to. */
export type AppOf<M extends AccessModule> = M extends ServicesModule ? 'services' : 'church'

export const ladderOf = (module: AccessModule): Ladder =>
  module === 'planning' || module === 'songs' ? module : 'church'

export const appOf = (module: AccessModule): AccessApp =>
  (ACCESS_MODULES.services as readonly string[]).includes(module) ? 'services' : 'church'

export const isAccessModule = (value: string): value is AccessModule =>
  (ACCESS_MODULE_IDS as readonly string[]).includes(value)

/** Whether `value` is a rung on this module's ladder. */
export const isLevelOf = <M extends AccessModule>(module: M, value: string): value is LevelOf<M> =>
  (LADDERS[ladderOf(module)] as readonly string[]).includes(value)

export const LEVEL_LABELS: Record<Level, string> = {
  scheduled_viewer: 'Scheduled viewer',
  viewer: 'Viewer',
  scheduler: 'Scheduler',
  editor: 'Editor',
  administrator: 'Administrator',
}

export const ACCESS_MODULE_LABELS: Record<AccessModule, string> = {
  planning: 'Planning & people',
  songs: 'Songs',
  // `org` is the church itself: its profile, campuses, staff, billing and integrations.
  org: 'Organization',
  people: 'People',
  groups: 'Groups',
  attendance: 'Attendance',
  events: 'Events',
  checkin: 'Check-in',
  messages: 'Messages',
  media: 'Media',
  reports: 'Reports',
}

/** The two apps whose modules are on a ladder, as the access screens name them. */
export const ACCESS_APP_LABELS: Record<AccessApp, string> = {
  services: 'ForServices',
  church: 'ForChurch',
}

/**
 * What each rung adds, module by module, as the access screens explain it.
 * A ForChurch module's Administrator gives access to it, up to their own
 * level and church-wide; only an Owner or Admin gives Organization
 * Administrator ("give any access").
 */
export const LEVEL_DESCRIPTIONS: { [M in AccessModule]: Record<LevelOf<M>, string> } = {
  planning: {
    scheduled_viewer: 'Only plans they are scheduled on, and Music Stand',
    viewer: 'Every plan in scope, read-only',
    scheduler: 'Schedule people onto teams and send requests',
    editor: 'Order, times, notes, files and series; browse songs to add',
    administrator: 'Service type settings and teams; give access within scope',
  },
  songs: {
    viewer: 'The song library, read-only',
    editor: 'Add and change songs and arrangements',
    administrator: 'Delete and merge songs; PraiseCharts imports',
  },
  org: {
    viewer: 'The church’s settings, campuses and staff, read-only',
    editor: 'Change the church’s profile and campuses',
    administrator: 'Staff and roles, billing, API keys, the audit log; give access',
  },
  people: {
    viewer: 'The directory and each person’s profile',
    editor: 'Add and change people, households, notes, tags and lists',
    administrator: 'Delete and merge people; custom fields, imports; give access',
  },
  groups: {
    viewer: 'Every group, its details and schedule',
    editor: 'Create, change and archive any group and its roster',
    administrator: 'Group types and tags; delete groups; give access',
  },
  attendance: {
    viewer: 'Headcounts and attendance records',
    editor: 'Record and correct headcounts and attendance',
    administrator: 'Delete recorded services; give access',
  },
  events: {
    viewer: 'The calendar and every event',
    editor: 'Create and change events, room bookings and registrations',
    administrator: 'Delete events; rooms, equipment, tags, calendar links; give access',
  },
  checkin: {
    viewer: 'Rooms and service times, read-only',
    editor: 'Run a station: find families and check children in',
    administrator: 'Rooms, times, stations, volunteers, background checks; give access',
  },
  messages: {
    viewer: 'Sent messages, the inbox and workflows, read-only',
    editor: 'Write, send and reply; build workflows',
    administrator: 'Sender settings and keyword auto-replies; give access',
  },
  media: {
    viewer: 'The media library, read-only',
    editor: 'Add and change media',
    administrator: 'Delete media; give access',
  },
  reports: {
    viewer: 'The dashboard and reports',
    editor: 'Save and schedule reports',
    administrator: 'Give access to Reports',
  },
}

/** What a rung of a module's ladder adds; empty off the ladder. */
export const levelDescription = (module: AccessModule, level: Level): string =>
  (LEVEL_DESCRIPTIONS[module] as Partial<Record<Level, string>>)[level] ?? ''

/** A level's place on the ladder: higher is more. */
export const levelRank = (level: Level): number => LEVELS.indexOf(level)

/** Whether `level` (null: no access) reaches `needed`. */
export const atLeast = <L extends Level>(level: L | null | undefined, needed: L): boolean =>
  level != null && levelRank(level) >= levelRank(needed)

/** The highest of some levels; null when there are none. */
export function highest<L extends Level>(levels: Iterable<L | null | undefined>): L | null {
  let best: L | null = null
  for (const level of levels)
    if (level != null && (best === null || levelRank(level) > levelRank(best))) best = level
  return best
}

// ---- grants ----------------------------------------------------------------------------

/** Where a grant applies: church-wide, a folder (and all inside it), or one service type. */
export type GrantScope =
  { kind: 'org' } | { kind: 'folder'; id: string } | { kind: 'service_type'; id: string }

export type GrantScopeKind = GrantScope['kind']

export const ORG_SCOPE = { kind: 'org' } as const satisfies GrantScope

type GrantOf<M extends AccessModule> = {
  app: AppOf<M>
  module: M
  level: LevelOf<M>
  // Folders and service types are ForServices planning scopes; the rest are church-wide.
  scope: M extends 'planning' ? GrantScope : typeof ORG_SCOPE
}

/** One rung of one module's ladder, for one person, somewhere. */
export type AccessGrant = { [M in AccessModule]: GrantOf<M> }[AccessModule]

/** Builds a grant, filling in the app. */
export function accessGrant<M extends AccessModule>(
  module: M,
  level: LevelOf<M>,
  scope: GrantOf<M>['scope'] = ORG_SCOPE,
): AccessGrant {
  return { app: appOf(module), module, level, scope } as AccessGrant
}

/**
 * A grant from loose values (a database row, a request body), or null when
 * they do not make one: an unknown module, a level off its ladder, an app
 * that is not the module's, or a folder scope on a church-wide module.
 */
export function parseAccessGrant(row: {
  app: string
  module: string
  level: string
  scopeType: string
  scopeId: string | null
}): AccessGrant | null {
  const { module, level, scopeType, scopeId } = row
  if (!isAccessModule(module) || row.app !== appOf(module) || !isLevelOf(module, level)) return null
  if (scopeType === 'org') return scopeId === null ? accessGrant(module, level) : null
  if (module !== 'planning' || !scopeId) return null
  if (scopeType === 'folder' || scopeType === 'service_type')
    return accessGrant('planning', level as PlanningLevel, { kind: scopeType, id: scopeId })
  return null
}

/**
 * What a person holds: their grants, and whether they are a church Owner or
 * Admin — Administrator on every ladder, everywhere, whatever the grants say.
 */
export interface Access {
  grants: readonly AccessGrant[]
  churchAdmin?: boolean
  /**
   * Their membership is limited to some campuses (a campus admin's). Their
   * ForChurch levels then hold only within those campuses, as roles always
   * did, so they cannot give a ForChurch grant: every one is church-wide.
   */
  campusLimited?: boolean
  /**
   * The teams they lead, with each team's service types (docs/14): a team
   * leader is Scheduler for the teams they lead, on those service types.
   * Only the rules about teams and seeing plans read it (`seeingLevel`,
   * `canScheduleTeam`); `effectiveLevel` is grants alone.
   */
  leading?: readonly LedTeam[]
}

/** A team someone leads, and the service types it belongs to (always at least one). */
export interface LedTeam {
  teamId: string
  serviceTypeIds: readonly string[]
}

// ---- resolution ------------------------------------------------------------------------

/**
 * Where a level is asked about. A service type carries the folders it sits
 * in, nearest first (empty at the root); a folder, the folders above it.
 * Songs and ForChurch modules are church-wide: their target is the church.
 */
export type AccessTarget =
  | { kind: 'org' }
  | { kind: 'folder'; id: string; ancestorIds: readonly string[] }
  | { kind: 'service_type'; id: string; folderIds: readonly string[] }

/** Whether a grant's scope reaches the target. */
function reaches(module: AccessModule, scope: GrantScope, target: AccessTarget): boolean {
  if (scope.kind === 'org') return true
  // Only planning has folders and service types; any other scope on another module is ignored.
  if (module !== 'planning') return false
  switch (target.kind) {
    case 'org':
      return false
    case 'folder':
      return (
        scope.kind === 'folder' && (scope.id === target.id || target.ancestorIds.includes(scope.id))
      )
    case 'service_type':
      return scope.kind === 'service_type'
        ? scope.id === target.id
        : target.folderIds.includes(scope.id)
  }
}

/**
 * A person's level on a module at a target: the highest of their church-wide
 * grant and every grant on the target or a folder above it. A root service
 * type has no folders, so only church-wide and its own grants count. Null
 * means no access at all.
 */
export function effectiveLevel<M extends AccessModule>(
  access: Access,
  module: M,
  target: AccessTarget = ORG_SCOPE,
): LevelOf<M> | null {
  if (access.churchAdmin) return 'administrator' as LevelOf<M>
  let best: LevelOf<M> | null = null
  for (const grant of access.grants) {
    if (grant.module !== module || !isLevelOf(module, grant.level)) continue
    if (!reaches(module, grant.scope, target)) continue
    if (best === null || levelRank(grant.level) > levelRank(best)) best = grant.level
  }
  return best ?? editorBrowsesSongs(access, module)
}

/**
 * An Editor of plans anywhere browses the song library (Songs Viewer) without
 * a songs grant: choosing songs for the order is an Editor's work, and the
 * library is church-wide, so there is no narrower place to give it.
 */
function editorBrowsesSongs<M extends AccessModule>(access: Access, module: M): LevelOf<M> | null {
  if (module !== 'songs') return null
  return atLeast(levelAnywhere(access, 'planning'), 'editor') ? ('viewer' as LevelOf<M>) : null
}

/**
 * A person's highest level on a module anywhere: church-wide, or on any
 * folder or service type. What a menu or a "+ New" button asks ("can they
 * edit a plan somewhere?"); a check on one plan asks `effectiveLevel` there.
 */
export function levelAnywhere<M extends AccessModule>(
  access: Access,
  module: M,
): LevelOf<M> | null {
  if (access.churchAdmin) return 'administrator' as LevelOf<M>
  const granted =
    highest(
      access.grants.flatMap((grant) =>
        grant.module === module && isLevelOf(module, grant.level) ? [grant.level] : [],
      ),
    ) ?? editorBrowsesSongs(access, module)
  // A team leader schedules somewhere: their own teams (docs/14).
  if (module !== 'planning' || (access.leading ?? []).length === 0) return granted
  return highest<PlanningLevel>([granted as PlanningLevel | null, 'scheduler']) as LevelOf<M>
}

/**
 * What a person holds, from loose rows (`/me`, a database): the rows the
 * rules refuse grant nothing.
 */
export function parseAccess(input: {
  churchAdmin?: boolean
  campusLimited?: boolean
  leading?: readonly { teamId: string; serviceTypeIds: readonly string[] }[]
  grants: readonly {
    app: string
    module: string
    level: string
    scopeType: string
    scopeId: string | null
  }[]
}): Access {
  return {
    churchAdmin: input.churchAdmin === true,
    campusLimited: input.campusLimited === true,
    leading: (input.leading ?? []).map((team) => ({
      teamId: team.teamId,
      serviceTypeIds: [...team.serviceTypeIds],
    })),
    grants: input.grants.flatMap((row) => {
      const grant = parseAccessGrant(row)
      return grant ? [grant] : []
    }),
  }
}

// ---- the church's default level (docs/14) ----------------------------------------------

/**
 * The Planning & people levels a church may give everyone by default:
 * Administrator is left out, since it would hand every member the keys to
 * give access.
 */
export const DEFAULT_PLANNING_LEVELS = [
  'scheduled_viewer',
  'viewer',
  'scheduler',
  'editor',
] as const satisfies readonly PlanningLevel[]
export type DefaultPlanningLevel = (typeof DEFAULT_PLANNING_LEVELS)[number]

/** Unless the church changes it: the plans they are scheduled on, and Music Stand. */
export const DEFAULT_PLANNING_LEVEL: DefaultPlanningLevel = 'scheduled_viewer'

export const isDefaultPlanningLevel = (value: string): value is DefaultPlanningLevel =>
  (DEFAULT_PLANNING_LEVELS as readonly string[]).includes(value)

/**
 * The grant every person in a church holds (devices aside): church-wide, at
 * the church's default level. A floor, never a cap: it is one more grant,
 * and the highest wins. A stored value off the list reads as the default.
 */
export const defaultPlanningGrant = (level: string = DEFAULT_PLANNING_LEVEL): AccessGrant =>
  accessGrant('planning', isDefaultPlanningLevel(level) ? level : DEFAULT_PLANNING_LEVEL)

// ---- folders and delegation ------------------------------------------------------------

/** Where folders and service types sit, as delegation needs it. */
export interface FolderTree {
  /** Each folder's parent; null for a folder at the root. */
  folders: ReadonlyMap<string, string | null>
  /** Each service type's folder; null for one at the root. */
  serviceTypes: ReadonlyMap<string, string | null>
}

/**
 * A folder and the folders above it, nearest first. Stops at an unknown
 * folder or a loop, so bad data can only make a chain shorter.
 */
export function folderChain(tree: FolderTree, folderId: string | null): string[] {
  const chain: string[] = []
  let id = folderId
  while (id !== null && tree.folders.has(id) && !chain.includes(id)) {
    chain.push(id)
    id = tree.folders.get(id) ?? null
  }
  return chain
}

/** The target a scope names, with its folder chain; null for an unknown folder or service type. */
export function scopeTarget(tree: FolderTree, scope: GrantScope): AccessTarget | null {
  switch (scope.kind) {
    case 'org':
      return ORG_SCOPE
    case 'folder':
      if (!tree.folders.has(scope.id)) return null
      return { kind: 'folder', id: scope.id, ancestorIds: folderChain(tree, scope.id).slice(1) }
    case 'service_type': {
      const folderId = tree.serviceTypes.get(scope.id)
      if (folderId === undefined) return null
      return { kind: 'service_type', id: scope.id, folderIds: folderChain(tree, folderId) }
    }
  }
}

/**
 * Whether the actor may give this grant. A church Owner or Admin may give
 * any. Anyone else must be Administrator of that module at the grant's
 * scope (there, or on a folder or church-wide above it) and may give up to
 * their own level there. Giving is not a ladder grant and never passes here.
 *
 * Two ForChurch exceptions (step F): Organization Administrator holds the
 * keys to the church (staff, roles, billing), so only an Owner or Admin
 * gives it; and someone limited to some campuses gives no ForChurch grant,
 * because each one is church-wide and would reach past their campuses.
 */
export function canGrant(actor: Access, proposed: AccessGrant, tree: FolderTree): boolean {
  // Loose data can reach here (a request body); a malformed grant is never allowed.
  const valid = parseAccessGrant({
    app: proposed.app,
    module: proposed.module,
    level: proposed.level,
    scopeType: proposed.scope.kind,
    scopeId: proposed.scope.kind === 'org' ? null : proposed.scope.id,
  })
  if (!valid) return false
  const target = scopeTarget(tree, proposed.scope)
  if (!target) return false
  const church = appOf(proposed.module) === 'church'
  if (church && actor.campusLimited) return false
  if (actor.churchAdmin) return true
  if (proposed.module === 'org' && proposed.level === 'administrator') return false
  const own = effectiveLevel(actor, proposed.module, target)
  return own === 'administrator' && atLeast<Level>(own, proposed.level)
}

/**
 * The modules access is given on, by app: ForServices', then ForChurch's.
 * Media is left out: its screens live in another app, so a grant on it
 * would do nothing here (members and the presets still carry it).
 */
export const ASSIGNABLE_MODULES: readonly AccessModule[] = [
  ...ACCESS_MODULES.services,
  ...ACCESS_MODULES.church.filter((module) => module !== 'media'),
]

/** A grant from its parts, whatever the module (the app filled in). */
export const grantOf = (module: AccessModule, level: Level, scope: GrantScope): AccessGrant =>
  ({ app: appOf(module), module, level, scope }) as AccessGrant

/** The rungs of a module's ladder the actor may give at this scope, lowest first. */
export function grantableLevels(
  actor: Access,
  module: AccessModule,
  scope: GrantScope,
  tree: FolderTree,
): Level[] {
  return LADDERS[ladderOf(module)].filter((level) =>
    canGrant(actor, grantOf(module, level, scope), tree),
  )
}

/** One key per module and place: a person holds at most one rung there. */
export const grantPlaceKey = (grant: Pick<AccessGrant, 'module' | 'scope'>): string =>
  `${grant.module}:${grant.scope.kind}:${grant.scope.kind === 'org' ? '' : grant.scope.id}`

// ---- plans, series and teams -----------------------------------------------------------

/** A plan is visible to a Viewer and above, or to a Scheduled viewer scheduled on it. */
export const canSeePlan = (level: PlanningLevel | null, scheduledOnIt: boolean): boolean =>
  atLeast(level, 'viewer') || (level !== null && scheduledOnIt)

/** A series is visible if any of its plans is. */
export const canSeeSeries = (
  plans: readonly { level: PlanningLevel | null; scheduledOnIt: boolean }[],
): boolean => plans.some((plan) => canSeePlan(plan.level, plan.scheduledOnIt))

/**
 * A series spanning several service types is editable only with Editor on
 * every one of them: pass the level on each service type its plans are in.
 * A series with no plans has nothing to be Editor on, so this says no; the
 * caller decides it on the church-wide level (pass just that).
 */
export const canEditSeries = (levels: readonly (PlanningLevel | null)[]): boolean =>
  levels.length > 0 && levels.every((level) => atLeast(level, 'editor'))

// ---- teams (docs/14-TEAMS-PEOPLE-TEMPLATES.md) ---------------------------------------------

/**
 * Whether one of the teams they lead serves at a service type. Every team
 * serves at least one (docs/14, 2026-10-21: there are no church-wide teams),
 * so leading never reaches the church as a whole or a folder.
 */
export function leadsOn(leading: readonly LedTeam[] | undefined, serviceTypeId: string) {
  return (leading ?? []).some((team) => team.serviceTypeIds.includes(serviceTypeId))
}

/** A level with leading counted: a team leader is at least Scheduler there. It never lowers one. */
export const withLeading = (level: PlanningLevel | null, leads: boolean): PlanningLevel | null =>
  leads ? highest<PlanningLevel>([level, 'scheduler']) : level

/**
 * Their planning level for seeing plans and teams at a target: their grants
 * (`effectiveLevel`), raised to Scheduler where a team they lead serves.
 * What a plan, a service type's list of plans or a team's visibility asks.
 * Scheduling is narrower: a leader schedules only their own teams
 * (`canScheduleTeam`), so scheduling checks ask `effectiveLevel` and the
 * team. The church and folders are not reached by leading.
 */
export function seeingLevel(
  access: Access,
  target: AccessTarget = ORG_SCOPE,
): PlanningLevel | null {
  const granted = effectiveLevel(access, 'planning', target)
  if (target.kind !== 'service_type') return granted
  return withLeading(granted, leadsOn(access.leading, target.id))
}

/** What the team rules ask about one team, from the viewer's side. */
export interface TeamView {
  /**
   * Their level on each of the team's service types, leading counted
   * (`seeingLevel`). A team serves at least one.
   */
  typeLevels: readonly (PlanningLevel | null)[]
  /** They are asked to serve on it (not declined) on a plan. */
  scheduledOnIt: boolean
  /** They lead it. */
  leadsIt: boolean
}

/**
 * Seeing a team: Viewer or above on any of its service types. A Scheduled
 * viewer sees only the teams they are scheduled on, and its leaders always
 * see it.
 */
export function canSeeTeam(team: TeamView): boolean {
  if (team.leadsIt) return true
  if (team.typeLevels.some((level) => atLeast(level, 'viewer'))) return true
  // Scheduled on it: a Scheduled viewer (someone with no level at all is not in ForServices).
  return team.scheduledOnIt && team.typeLevels.some((level) => level !== null)
}

/**
 * Changing a team (name, positions, leaders, default roster): Administrator
 * on every service type it belongs to — granted levels, as leading never
 * reaches Administrator. A team with no service types (none should exist)
 * is changed by nobody, rather than by everybody.
 */
export function canEditTeamSettings(team: {
  typeLevels: readonly (PlanningLevel | null)[]
}): boolean {
  return (
    team.typeLevels.length > 0 && team.typeLevels.every((level) => atLeast(level, 'administrator'))
  )
}

/**
 * Scheduling people onto a team on a plan: Scheduler granted on the plan's
 * service type (every team there), or leading this team when it serves
 * that service type.
 */
export function canScheduleTeam(input: {
  /** Their granted level on the plan's service type (`effectiveLevel`). */
  planLevel: PlanningLevel | null
  leadsIt: boolean
  /** The team belongs to the plan's service type. */
  servesThere: boolean
}): boolean {
  return atLeast(input.planLevel, 'scheduler') || (input.leadsIt && input.servesThere)
}

// ---- people seen from ForServices --------------------------------------------------------

/**
 * Phones, emails and address, seen from ForServices. Visible with the
 * separate contact permission, or to a Scheduler and above on a plan or
 * team where the person serves. A child's stay behind `people:minors:read`
 * as well. Basic details (name, photo, birthday, campus, status) are for
 * every ForServices user and need no rule.
 */
export function canSeeContactDetails(input: {
  /** The viewer holds the contact-details permission. */
  contactPermission: boolean
  /** The viewer's planning level on each plan or team the person serves on. */
  levelsWhereTheyServe: readonly (PlanningLevel | null)[]
  /** The person is a child. */
  minor: boolean
  /** The viewer holds `people:minors:read`. */
  minorsPermission: boolean
}): boolean {
  if (input.minor && !input.minorsPermission) return false
  return (
    input.contactPermission ||
    input.levelsWhereTheyServe.some((level) => atLeast(level, 'scheduler'))
  )
}

// ---- preset roles ----------------------------------------------------------------------

const everyModule = (level: 'viewer' | 'editor' | 'administrator'): AccessGrant[] =>
  ACCESS_MODULE_IDS.map((module) => accessGrant(module, level))

/**
 * The grants each preset role implies, for seeding (step C). Read, write and
 * admin become Viewer, Editor and Administrator; giving and the field
 * permissions stay on the role. Devices hold nothing on a ladder. Planning's
 * floor is not a role's: every person holds the church's default level
 * (`defaultPlanningGrant`, Scheduled viewer unless the church changes it).
 *
 * - Members drop from reading every plan to that default (13-ACCESS), and
 *   hold no songs grant: they see the songs on the plans they can see.
 * - Service schedulers could edit plans and songs (`services:write`), so they
 *   stay Editor on both.
 * - Campus admins administered ForServices church-wide (services were never
 *   campus-scoped); they keep that until someone narrows them to a folder.
 *
 * From step F these decide ForChurch too (`grantsForMember`). Checked
 * against each role's permissions in `ROLES`: every preset's ForChurch
 * levels give exactly the read, write and admin it held, with one
 * exception — service schedulers held `messages:write` without
 * `messages:read`; as Messages Editors they now read Messages too.
 */
export const PRESET_GRANTS: Record<RoleId, readonly AccessGrant[]> = {
  owner: everyModule('administrator'),
  admin: everyModule('administrator'),
  staff: [
    ...everyModule('editor').filter((grant) => grant.module !== 'org'),
    accessGrant('org', 'viewer'),
  ],
  campus_admin: everyModule('administrator'),
  finance: [accessGrant('org', 'viewer'), accessGrant('reports', 'viewer')],
  group_leader: [
    accessGrant('groups', 'viewer'),
    accessGrant('attendance', 'editor'),
    accessGrant('people', 'viewer'),
  ],
  service_scheduler: [
    accessGrant('planning', 'editor'),
    accessGrant('songs', 'editor'),
    accessGrant('people', 'viewer'),
    accessGrant('messages', 'editor'),
  ],
  checkin_volunteer: [accessGrant('checkin', 'editor'), accessGrant('people', 'viewer')],
  checkin_station: [],
  api_client: [],
  member: [
    accessGrant('people', 'viewer'),
    accessGrant('groups', 'viewer'),
    accessGrant('events', 'viewer'),
    accessGrant('media', 'viewer'),
  ],
}

const CHURCH_ADMIN_ROLES: readonly string[] = ['owner', 'admin']

/** Church Owner or Admin: Administrator everywhere. */
export const isChurchAdmin = (roleIds: readonly string[]): boolean =>
  roleIds.some((id) => CHURCH_ADMIN_ROLES.includes(id))

/**
 * What a membership's preset roles add up to: one grant per module and
 * scope, the highest of them. Custom roles carry nothing here; role
 * templates replace them.
 */
export function accessForRoles(roleIds: readonly string[]): Access {
  const best = new Map<string, AccessGrant>()
  for (const id of roleIds) {
    if (!Object.hasOwn(PRESET_GRANTS, id)) continue
    for (const grant of PRESET_GRANTS[id as RoleId]) {
      const key = `${grant.module}:${grant.scope.kind}:${grant.scope.kind === 'org' ? '' : grant.scope.id}`
      const held = best.get(key)
      if (!held || levelRank(grant.level) > levelRank(held.level)) best.set(key, grant)
    }
  }
  return { grants: [...best.values()], churchAdmin: isChurchAdmin(roleIds) }
}

const SCOPE_LEVELS = { read: 'viewer', write: 'editor', admin: 'administrator' } as const

/**
 * What an API key's fixed scopes mean on the ladders. Keys keep their own
 * scopes (13-ACCESS: outside the ladder); this only reads them the way the
 * presets were read: `services:read` is Viewer of planning and songs
 * church-wide, `write` Editor, `admin` Administrator, and a ForChurch
 * module's scopes the same rungs of its own ladder. A key is never a church
 * Owner or Admin and holds no default level.
 */
export function accessForScopes(scopes: readonly string[]): Access {
  const grants: AccessGrant[] = []
  for (const scope of scopes) {
    const [module, action] = scope.split(':')
    if (!module || !action || !Object.hasOwn(SCOPE_LEVELS, action)) continue
    const level = SCOPE_LEVELS[action as keyof typeof SCOPE_LEVELS]
    if (module === 'services') {
      grants.push(accessGrant('planning', level), accessGrant('songs', level))
    } else if (isAccessModule(module) && ladderOf(module) === 'church') {
      grants.push(accessGrant(module, level))
    }
  }
  return { grants, churchAdmin: false }
}

// ---- ForChurch permissions from the ladders (step F) -----------------------------------

const ACTIONS = ['read', 'write', 'admin'] as const

/**
 * Whether a permission is a ForChurch module's read, write or admin — the
 * ladder decides those. Giving, ForServices' `services:*` and the field
 * permissions (`people:minors:read`, …) are not: roles keep them.
 */
export function isLadderPermission(permission: string): boolean {
  const [module, action, extra] = permission.split(':')
  return (
    extra === undefined &&
    (ACCESS_MODULES.church as readonly string[]).includes(module ?? '') &&
    (ACTIONS as readonly string[]).includes(action ?? '')
  )
}

/**
 * The ForChurch permissions a person's levels give, church-wide: Viewer
 * reads, Editor also writes, Administrator also administers. What
 * `requirePermission('people:write')` and `can()` then check.
 */
export function ladderPermissions(access: Access): Permission[] {
  return ACCESS_MODULES.church.flatMap((module) => {
    const level = effectiveLevel(access, module)
    return ACTIONS.filter((action) => atLeast<Level>(level, SCOPE_LEVELS[action])).map(
      (action) => `${module}:${action}` as Permission,
    )
  })
}
